import { useState } from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Textarea } from '@zt/ui';
import type { ai } from '@zt/core';
import { localeName } from '../../components/ContentLocale';
import { ProductMultiPicker } from '../../components/ProductMultiPicker';
import { api, newIdempotencyKey, type Jsonify } from '../../lib/api';
import { HelpTip } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { fieldError, useApiMutation } from '../../lib/mutation';
import { usePermissions } from '../../lib/session';
import { AiButton, useAiAvailability } from './shared';

/**
 * Yapay zekâ taslak düğmeleri (F9-07): ilgili ekranlara tek satırla eklenir. Hiçbiri kaydetmez; sonuç forma
 * doldurulur ve kullanıcı kontrol edip kaydeder. Sağlık beyanı bulunursa uyarı gösterilir.
 */

type R<F extends (...a: never[]) => unknown> = Jsonify<Awaited<ReturnType<F>>>;
type Finding = { match: string };

const post = <T,>(path: string, body: unknown) =>
  api.post<T>(path, body, { idempotencyKey: newIdempotencyKey() });

function ClaimsNote({ findings }: { findings: Finding[] }) {
  const t = useT();
  if (!findings.length) return null;
  return (
    <p role="status" className="rounded-md border border-amber/40 bg-amber/5 p-2 text-sm">
      {t('ai.studio.claimsFound', { count: findings.length })}{' '}
      <span className="text-ink-2">{[...new Set(findings.map((c) => `"${c.match}"`))].join(', ')}</span>
    </p>
  );
}

function CopyButton({ text }: { text: string }) {
  const t = useT();
  return (
    <Button
      size="sm"
      variant="ghost"
      aria-label={t('ai.drafts.copy')}
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(
          () => toast.success(t('ai.drafts.copied')),
          () => undefined,
        );
      }}
    >
      <Copy className="h-4 w-4" aria-hidden />
    </Button>
  );
}

// ------------------------------------------------------------------------------------------- Ürün açıklaması

type ProductDesc = R<typeof ai.productDescriptionDraft>;

export function ProductDescriptionDraft({
  productId,
  onApply,
}: {
  productId: string;
  onApply: (v: { shortDescription: string; description: string }) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [tone, setTone] = useState<'informative' | 'friendly'>('informative');
  const [notes, setNotes] = useState('');
  const gen = useApiMutation(
    () =>
      post<ProductDesc>('/v1/admin/ai/drafts/product-description', {
        productId,
        tone,
        notes: notes.trim() || undefined,
      }),
    { invalidate: [['ai', 'availability']] },
  );
  const r = gen.data;
  return (
    <span className="inline-flex items-center gap-1">
      <AiButton onClick={() => setOpen(true)}>{t('ai.drafts.productDescription')}</AiButton>
      <HelpTip k="ai.draft_product" />
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('ai.drafts.productDescription')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            {r ? (
              <Button
                variant="primary"
                onClick={() => {
                  onApply({ shortDescription: r.shortDescription, description: r.description });
                  setOpen(false);
                  toast.success(t('ai.drafts.applied'));
                }}
              >
                {t('ai.drafts.apply')}
              </Button>
            ) : null}
            <Button
              variant={r ? 'secondary' : 'primary'}
              loading={gen.isPending}
              onClick={() => gen.mutate(undefined)}
            >
              {r ? t('ai.drafts.again') : t('ai.drafts.generate')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-2">{t('ai.drafts.productLead')}</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('ai.drafts.tone')}>
              {(x) => (
                <Select {...x} value={tone} onChange={(e) => setTone(e.target.value as typeof tone)}>
                  <option value="informative">{t('ai.drafts.tones.informative')}</option>
                  <option value="friendly">{t('ai.drafts.tones.friendly')}</option>
                </Select>
              )}
            </Field>
            <Field label={t('ai.drafts.notes')}>
              {(x) => (
                <Input {...x} value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
              )}
            </Field>
          </div>
          {r && (
            <div className="space-y-3 rounded-md border border-line p-3 text-sm">
              <ClaimsNote findings={r.claimFindings} />
              <p className="font-medium">{t('admin.products.shortDescription')}</p>
              <p className="whitespace-pre-line">{r.shortDescription}</p>
              <p className="font-medium">{t('admin.products.description')}</p>
              <p className="whitespace-pre-line">{r.description}</p>
              <p className="text-ink-3">{t('ai.drafts.reviewNote')}</p>
            </div>
          )}
        </div>
      </Dialog>
    </span>
  );
}

// ------------------------------------------------------------------------------------------- Blog çevirisi

type PostTranslation = R<typeof ai.translatePostDraft>;

export function PostTranslateDraft({
  postId,
  sources,
  onApply,
}: {
  postId: string;
  sources: Array<{ id: string; locale: string }>;
  onApply: (v: PostTranslation) => void;
}) {
  const t = useT();
  const [from, setFrom] = useState(sources[0]?.id ?? '');
  const gen = useApiMutation(
    () => post<PostTranslation>('/v1/admin/ai/drafts/post-translation', { postId, fromPostId: from }),
    {
      invalidate: [['ai', 'availability']],
      success: t('ai.drafts.translated'),
      onSuccess: (r) => onApply(r),
    },
  );
  if (!sources.length) return null;
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Field label={t('ai.drafts.translateFrom')} className="w-48">
        {(x) => (
          <Select {...x} value={from} onChange={(e) => setFrom(e.target.value)}>
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {localeName(s.locale)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <AiButton loading={gen.isPending} onClick={() => gen.mutate(undefined)}>
        {t('ai.drafts.translate')}
      </AiButton>
      <HelpTip k="ai.draft_translate" />
      {gen.data && <ClaimsNote findings={gen.data.claimFindings} />}
    </div>
  );
}

// ------------------------------------------------------------------------------------------- E-posta

type EmailCopy = R<typeof ai.emailCopyDraft>;

export function EmailCopyDraft({
  locale,
  onApply,
}: {
  locale: string;
  onApply: (v: { subject?: string; subjectB?: string; preheader?: string }) => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [goal, setGoal] = useState('');
  const [audience, setAudience] = useState('');
  const [productIds, setProductIds] = useState<string[]>([]);
  const gen = useApiMutation(
    () =>
      post<EmailCopy>('/v1/admin/ai/drafts/email', {
        goal,
        locale,
        audience: audience.trim() || undefined,
        productIds,
      }),
    { invalidate: [['ai', 'availability']] },
  );
  const r = gen.data;
  const apply = (v: Parameters<typeof onApply>[0]) => {
    onApply(v);
    toast.success(t('ai.drafts.applied'));
  };
  return (
    <span className="inline-flex items-center gap-1">
      <AiButton onClick={() => setOpen(true)}>{t('ai.drafts.email')}</AiButton>
      <HelpTip k="ai.draft_email" />
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('ai.drafts.email')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.close')}</Button>
            <Button
              variant="primary"
              disabled={goal.trim().length < 10}
              loading={gen.isPending}
              onClick={() => gen.mutate(undefined)}
            >
              {r ? t('ai.drafts.again') : t('ai.drafts.generate')}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field
            label={t('ai.drafts.goal')}
            description={t('ai.drafts.goalHint')}
            error={fieldError(gen.error, 'goal')}
          >
            {(x) => (
              <Textarea
                {...x}
                rows={2}
                value={goal}
                maxLength={500}
                onChange={(e) => setGoal(e.target.value)}
              />
            )}
          </Field>
          <Field label={t('ai.drafts.audience')}>
            {(x) => (
              <Input {...x} value={audience} maxLength={200} onChange={(e) => setAudience(e.target.value)} />
            )}
          </Field>
          <ProductMultiPicker
            value={productIds}
            onChange={setProductIds}
            label={t('ai.drafts.products')}
            max={5}
          />
          {r && (
            <div className="space-y-3 rounded-md border border-line p-3 text-sm">
              <ClaimsNote findings={r.claimFindings} />
              <p className="font-medium">{t('ai.drafts.subjects')}</p>
              <ul className="space-y-1">
                {r.subjects.map((s) => (
                  <li key={s} className="flex flex-wrap items-center gap-2">
                    <span className="flex-1">{s}</span>
                    <Button size="sm" variant="ghost" onClick={() => apply({ subject: s })}>
                      {t('ai.drafts.useAsA')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => apply({ subjectB: s })}>
                      {t('ai.drafts.useAsB')}
                    </Button>
                  </li>
                ))}
              </ul>
              <p className="font-medium">{t('ai.drafts.preheader')}</p>
              <p className="flex flex-wrap items-center gap-2">
                <span className="flex-1">{r.preheader}</span>
                <Button size="sm" variant="ghost" onClick={() => apply({ preheader: r.preheader })}>
                  {t('ai.drafts.use')}
                </Button>
              </p>
              <p className="font-medium">{t('ai.drafts.intro')}</p>
              <p className="flex items-start gap-2">
                <span className="flex-1 whitespace-pre-line">{r.intro}</span>
                <CopyButton text={r.intro} />
              </p>
              <p className="text-ink-3">{t('ai.drafts.introNote')}</p>
            </div>
          )}
        </div>
      </Dialog>
    </span>
  );
}

// ------------------------------------------------------------------------------------------- Destek yanıtı

type SupportReply = R<typeof ai.supportReplyDraft>;

export function SupportReplyDraft({
  ticketId,
  onDraft,
}: {
  ticketId: string;
  onDraft: (text: string) => void;
}) {
  const t = useT();
  const { can } = usePermissions();
  const av = useAiAvailability();
  const gen = useApiMutation(() => post<SupportReply>('/v1/admin/ai/drafts/support-reply', { ticketId }), {
    invalidate: [['ai', 'availability']],
    success: t('ai.drafts.replyDone'),
    onSuccess: (r) => onDraft(r.reply),
  });
  // Sahip destek taslaklarını açmadıysa düğme hiç görünmez (müşteri mesajı dış hizmete gitmez).
  if (!can('ai.generate') || !av.data?.supportDrafts) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <AiButton loading={gen.isPending} onClick={() => gen.mutate(undefined)}>
        {t('ai.drafts.supportReply')}
      </AiButton>
      <HelpTip k="ai.draft_support" />
      {gen.data && <ClaimsNote findings={gen.data.claimFindings} />}
    </span>
  );
}

// ------------------------------------------------------------------------------------------- Reklam

type AdCopy = R<typeof ai.adCopyDraft>;
type AdSuggest = R<typeof ai.adSuggestions>;

/** Reklam merkezi bölümleri: kurallı reklam önerileri (+ isteğe bağlı yapay zekâ özeti) ve reklam metni taslağı. */
export function AdAiSections() {
  const { can } = usePermissions();
  return (
    <>
      <AdSuggestionsSection />
      {can('marketing.ads.manage') && can('ai.generate') && <AdCopySection />}
    </>
  );
}

function AdSuggestionsSection() {
  const t = useT();
  const { formatNumber } = useI18n();
  const { can } = usePermissions();
  const [days, setDays] = useState(30);
  const run = useApiMutation(
    (withAi: boolean) => post<AdSuggest>('/v1/admin/ai/drafts/ad-suggestions', { days, withAi }),
    { invalidate: [['ai', 'availability']] },
  );
  const r = run.data;
  return (
    <Section title={t('ai.drafts.adSuggestions')} action={<HelpTip k="ai.ad_suggestions" />}>
      <p className="mb-3 max-w-[70ch] text-sm text-ink-2">{t('ai.drafts.adSuggestionsLead')}</p>
      <div className="flex flex-wrap items-end gap-2">
        <Field label={t('ai.drafts.period')} className="w-40">
          {(x) => (
            <Select {...x} value={days} onChange={(e) => setDays(Number(e.target.value))}>
              {[7, 30, 90].map((d) => (
                <option key={d} value={d}>
                  {t('ai.drafts.lastDays', { days: d })}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Button loading={run.isPending && run.variables === false} onClick={() => run.mutate(false)}>
          {t('ai.drafts.findings')}
        </Button>
        {can('ai.generate') && (
          <AiButton loading={run.isPending && run.variables === true} onClick={() => run.mutate(true)}>
            {t('ai.drafts.summarize')}
          </AiButton>
        )}
      </div>
      {r &&
        (r.status === 'no_data' ? (
          <p className="mt-3 text-sm text-ink-2">{t('ai.drafts.noAdData')}</p>
        ) : !r.findings.length ? (
          <p className="mt-3 text-sm text-ink-2">{t('ai.drafts.noFindings')}</p>
        ) : (
          <ul className="mt-3 space-y-1.5 text-sm">
            {r.findings.map((f, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2">
                <Badge tone={f.kind === 'scale_candidate' ? 'moss' : 'amber'}>
                  {t(`ai.drafts.findingKinds.${f.kind}`)}
                </Badge>
                <span>
                  {f.platform} · {f.campaignName || '—'}
                  {f.roasBps !== null && ` · ROAS ${formatNumber(f.roasBps / 10_000, 2)}`}
                  {f.ctrBps !== null && ` · CTR %${formatNumber(f.ctrBps / 100, 2)}`}
                  {f.detail && ` · ${t(`ai.drafts.anomalies.${f.detail}`)}`}
                </span>
              </li>
            ))}
          </ul>
        ))}
      {r && 'summary' in r && r.summary && (
        <div className="mt-4 rounded-md border border-line p-3 text-sm">
          <p className="mb-1 font-medium">{t('ai.drafts.aiSummary')}</p>
          <p className="whitespace-pre-line">{r.summary}</p>
          <p className="mt-2 text-ink-3">{t('ai.drafts.adChangeNote')}</p>
        </div>
      )}
    </Section>
  );
}

function AdCopySection() {
  const t = useT();
  const [productIds, setProductIds] = useState<string[]>([]);
  const [platform, setPlatform] = useState<'google' | 'meta'>('google');
  const gen = useApiMutation(
    () => post<AdCopy>('/v1/admin/ai/drafts/ad-copy', { productId: productIds[0], platform }),
    { invalidate: [['ai', 'availability']] },
  );
  const r = gen.data;
  const group = (title: string, items: string[], max: number) =>
    items.length > 0 && (
      <div>
        <p className="mb-1 font-medium">
          {title} <span className="font-normal text-ink-3">({t('ai.drafts.maxChars', { max })})</span>
        </p>
        <ul className="space-y-1">
          {items.map((x) => (
            <li key={x} className="flex items-center gap-2">
              <span className="flex-1">{x}</span>
              <span className="tabular-nums text-ink-3">{x.length}</span>
              <CopyButton text={x} />
            </li>
          ))}
        </ul>
      </div>
    );
  return (
    <Section title={t('ai.drafts.adCopy')} action={<HelpTip k="ai.draft_ad_copy" />}>
      <div className="grid gap-4 sm:grid-cols-2">
        <ProductMultiPicker
          value={productIds}
          onChange={setProductIds}
          label={t('ai.drafts.product')}
          max={1}
        />
        <Field label={t('ai.drafts.platform')}>
          {(x) => (
            <Select {...x} value={platform} onChange={(e) => setPlatform(e.target.value as typeof platform)}>
              <option value="google">{t('ai.drafts.platforms.google')}</option>
              <option value="meta">{t('ai.drafts.platforms.meta')}</option>
            </Select>
          )}
        </Field>
      </div>
      <div className="mt-3">
        {productIds.length ? (
          <AiButton loading={gen.isPending} onClick={() => gen.mutate(undefined)}>
            {t('ai.drafts.generate')}
          </AiButton>
        ) : (
          <p className="text-sm text-ink-3">{t('ai.drafts.pickProduct')}</p>
        )}
      </div>
      {gen.isPending && <Spinner label={t('common.loading')} />}
      {r && (
        <div className="mt-4 space-y-4 rounded-md border border-line p-3 text-sm">
          <ClaimsNote findings={r.claimFindings} />
          {group(t('ai.drafts.primaryTexts'), r.primaryTexts, 125)}
          {group(t('ai.drafts.headlines'), r.headlines, r.platform === 'google' ? 30 : 40)}
          {group(t('ai.drafts.descriptions'), r.descriptions, r.platform === 'google' ? 90 : 30)}
          {r.droppedOverLimit > 0 && (
            <p className="text-ink-3">{t('ai.drafts.dropped', { count: r.droppedOverLimit })}</p>
          )}
          {!r.headlines.length && !r.descriptions.length && !r.primaryTexts.length && (
            <EmptyState title={t('ai.drafts.adEmpty')} description={t('ai.drafts.adEmptyLead')} />
          )}
          <p className="text-ink-3">{t('ai.drafts.adChangeNote')}</p>
        </div>
      )}
    </Section>
  );
}
